import { useState, useEffect, useMemo } from 'react';
import type React from 'react';

import {
  Alert,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  HelpTip,
  Input,
  Label,
  Select,
  Switch,
} from '@/components/ui';
import {
  AVISO_ENTIDADE_SEM_TELA,
  ENTIDADES_SUPORTADAS,
  GRUPOS_TIPOS,
  TIPOS_DISPONIVEIS,
  TIPOS_EM_PREPARACAO,
  useAdminCustomFieldsActions,
  useAdminCustomValidators,
  type AdminCustomField,
  type AdminCustomFieldInput,
  type FieldTipo,
  type Largura,
  type ScopeTipo,
} from './store';
// PF9/K-01: a lista de tipos aceitos no formulário público é UMA só, dividida
// com o backend (`server/routes/public-captacao.ts`). Se o construtor tiver a
// sua própria cópia, ele volta a prometer ao admin um tipo que a captação não
// desenha — foi o bug que originou a rodada.
import { isTipoPublico } from '@server-types/custom-fields-publico';

/**
 * CampoBuilderDialog (A6.4).
 *
 * Wizard de criação/edição consolidado em 1 Dialog com 4 sub-steps
 * navegáveis por tabs internas. Decisão de design (vs 4 dialogs):
 *   - menos navegação pro admin (vê tudo num só lugar)
 *   - reusa controles UI já existentes
 *   - validação por step na ordem (Step1 deve estar válido antes de avançar)
 *
 * Steps:
 *   1. Básico — entidade + scope + key + label + tipo
 *   2. Validação — validacaoJson + customValidator dropdown
 *   3. Opções — só pra dropdown/multi_select/radio (lista editável)
 *   4. UI — placeholder + prefix + suffix + largura + ordem + grupo +
 *           visibilidade por perfis
 *
 * NÃO REVERTER:
 *   - Steps 2/3/4 são OPCIONAIS — campo pode ser salvo com só Step1
 *   - key auto-gerada do label (slug snake_case) pode ser editada
 *   - Step3 só aparece se tipo IN (dropdown, multi_select, radio)
 *   - O seletor de tipo NÃO oferece `TIPOS_EM_PREPARACAO` (Arquivo/Imagem —
 *     sem endpoint de upload). Um campo JÁ salvo num deles reaparece só na
 *     própria edição, desabilitado e avisado — nunca troca de tipo sozinho
 */
export interface CampoBuilderDialogProps {
  mode: 'create' | 'edit';
  field?: AdminCustomField;
  onClose: () => void;
}

type Step = 1 | 2 | 3 | 4;

interface OpcaoItem {
  value: string;
  label: string;
}

const PERFIS_DISPONIVEIS = [
  { value: 'admin', label: 'Admin' },
  { value: 'comercial', label: 'Comercial' },
  { value: 'manutencao', label: 'Manutenção' },
  { value: 'tecnico', label: 'Técnico' },
  { value: 'administrativo', label: 'Administrativo' },
  { value: 'cliente_gestor', label: 'Cliente Gestor' },
  { value: 'cliente_portaria', label: 'Cliente Portaria' },
];

function labelToKey(label: string): string {
  return label
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // remove acentos
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64);
}

export function CampoBuilderDialog({ mode, field, onClose }: CampoBuilderDialogProps) {
  const [step, setStep] = useState<Step>(1);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Step 1
  //
  // K-02: o padrão era 'deal' — o único dos quatro lugares decididos que NÃO
  // tem tela pra preencher. Quem clicava "Novo campo" e ia direto no Salvar
  // criava, sem saber, um campo que ninguém consegue responder. O padrão passa
  // a ser 'lead', o cadastro com mais superfície (ficha interna + captação).
  const [entidade, setEntidade] = useState(field?.entidade ?? 'lead');
  const [scopeTipo, setScopeTipo] = useState<ScopeTipo>(field?.scopeTipo ?? 'global');
  const [scopeId, setScopeId] = useState(field?.scopeId ?? '');
  const [key, setKey] = useState(field?.key ?? '');
  const [label, setLabel] = useState(field?.label ?? '');
  const [descricao, setDescricao] = useState(field?.descricao ?? '');
  const [tipo, setTipo] = useState<FieldTipo>(field?.tipo ?? 'text');

  // PF9 — exposição no formulário público de captação (só relevante em lead).
  const [mostrarNoFormPublico, setMostrarNoFormPublico] = useState(
    field?.mostrarNoFormPublico === 1,
  );
  const [obrigatorioNoFormPublico, setObrigatorioNoFormPublico] = useState(
    field?.obrigatorioNoFormPublico === 1,
  );

  // Step 2
  const [validacaoMin, setValidacaoMin] = useState<string>('');
  const [validacaoMax, setValidacaoMax] = useState<string>('');
  const [validacaoRegex, setValidacaoRegex] = useState<string>('');
  const [validatorId, setValidatorId] = useState<string>('');

  // Step 3 — opções pra dropdown/multi/radio
  const [opcoes, setOpcoes] = useState<OpcaoItem[]>([]);

  // Step 4
  const [placeholder, setPlaceholder] = useState(field?.placeholder ?? '');
  const [prefix, setPrefix] = useState(field?.prefix ?? '');
  const [suffix, setSuffix] = useState(field?.suffix ?? '');
  const [largura, setLargura] = useState<Largura>(field?.largura ?? 'full');
  const [ordem, setOrdem] = useState(field?.ordem ?? 0);
  const [grupo, setGrupo] = useState(field?.grupo ?? '');
  const [visiveis, setVisiveis] = useState<string[]>([]);
  const [editaveis, setEditaveis] = useState<string[]>([]);

  const { data: validators = [] } = useAdminCustomValidators();
  const { create, update } = useAdminCustomFieldsActions();

  // Inicializa state from field (edit mode)
  useEffect(() => {
    if (!field) return;
    try {
      const v = field.validacaoJson ? JSON.parse(field.validacaoJson) : {};
      setValidacaoMin(v.min !== undefined ? String(v.min) : '');
      setValidacaoMax(v.max !== undefined ? String(v.max) : '');
      setValidacaoRegex(v.regex ?? '');
      setValidatorId(v.customValidatorId ?? '');
    } catch { /* ignora */ }
    try {
      const o = field.opcoesJson ? JSON.parse(field.opcoesJson) : [];
      if (Array.isArray(o)) setOpcoes(o);
    } catch { /* ignora */ }
    try {
      const vis = field.visivelParaPerfisJson ? JSON.parse(field.visivelParaPerfisJson) : null;
      if (Array.isArray(vis)) setVisiveis(vis);
    } catch { /* ignora */ }
    try {
      const ed = field.editavelParaPerfisJson ? JSON.parse(field.editavelParaPerfisJson) : null;
      if (Array.isArray(ed)) setEditaveis(ed);
    } catch { /* ignora */ }
  }, [field]);

  // Auto-gera key do label se vazia (modo create)
  useEffect(() => {
    if (mode === 'create' && !key && label) {
      setKey(labelToKey(label));
    }
  }, [mode, label, key]);

  const needsOpcoes = tipo === 'dropdown' || tipo === 'multi_select' || tipo === 'radio';

  // K-02 — "onde este campo vai aparecer": o construtor prometia 10 cadastros
  // e só 4 desenham campo personalizado em alguma tela.
  const entidadeMeta = ENTIDADES_SUPORTADAS.find((e) => e.value === entidade);

  // FASE-C: o seletor não oferece os tipos sem caminho de ponta a ponta.
  // Se o campo que está sendo editado JÁ usa um deles, o tipo volta pra
  // grade — desabilitado — pra a tela não ficar sem seleção nem o próximo
  // "Salvar" trocar o tipo do campo por conta própria.
  const tipoEmPreparacao = TIPOS_EM_PREPARACAO.includes(tipo);
  const tiposOferecidos = useMemo(
    () =>
      TIPOS_DISPONIVEIS.filter(
        (t) => !TIPOS_EM_PREPARACAO.includes(t.value) || t.value === tipo,
      ),
    [tipo],
  );

  function isStep1Valid(): boolean {
    if (!entidade.trim() || !label.trim() || !key.trim()) return false;
    if (!/^[a-z][a-z0-9_]*$/.test(key)) return false;
    if (scopeTipo !== 'global' && !scopeId.trim()) return false;
    return true;
  }

  function buildValidacaoJson(): string | null {
    const v: Record<string, unknown> = {};
    if (validacaoMin) v.min = Number(validacaoMin);
    if (validacaoMax) v.max = Number(validacaoMax);
    if (validacaoRegex) v.regex = validacaoRegex;
    if (validatorId) v.customValidatorId = validatorId;
    return Object.keys(v).length > 0 ? JSON.stringify(v) : null;
  }

  async function handleSubmit() {
    if (!isStep1Valid()) {
      setErrorMsg(
        'Em "Básico", preencha o cadastro, o nome do campo e o identificador (só letras minúsculas, números e _).',
      );
      setStep(1);
      return;
    }
    if (needsOpcoes && opcoes.length === 0) {
      setErrorMsg('Em "Opções", cadastre pelo menos uma opção de escolha.');
      setStep(3);
      return;
    }
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const payload: AdminCustomFieldInput = {
        entidade,
        scopeTipo,
        scopeId: scopeTipo === 'global' ? null : scopeId,
        key,
        label,
        descricao: descricao || null,
        tipo,
        opcoesJson: needsOpcoes && opcoes.length > 0 ? JSON.stringify(opcoes) : null,
        validacaoJson: buildValidacaoJson(),
        placeholder: placeholder || null,
        prefix: prefix || null,
        suffix: suffix || null,
        largura,
        ordem,
        grupo: grupo || null,
        visivelParaPerfisJson: visiveis.length > 0 ? JSON.stringify(visiveis) : null,
        editavelParaPerfisJson: editaveis.length > 0 ? JSON.stringify(editaveis) : null,
        // PF9 — só faz sentido em lead; ligado em outras entidades é inócuo
        // (o form público filtra por entidade='lead'), mas persistimos o que a UI mostra.
        mostrarNoFormPublico: entidade === 'lead' ? mostrarNoFormPublico : false,
        obrigatorioNoFormPublico:
          entidade === 'lead' ? mostrarNoFormPublico && obrigatorioNoFormPublico : false,
      };
      if (mode === 'create') {
        await create.mutateAsync(payload);
      } else if (field) {
        await update.mutateAsync({ id: field.id, patch: payload });
      }
      onClose();
    } catch (e) {
      setErrorMsg(
        e instanceof Error
          ? `Não foi possível salvar o campo. Detalhe: ${e.message}`
          : 'Não foi possível salvar o campo. Tente de novo.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  function togglePerfil(list: string[], setList: (l: string[]) => void, perfil: string) {
    if (list.includes(perfil)) {
      setList(list.filter((p) => p !== perfil));
    } else {
      setList([...list, perfil]);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} size="xl">
      <DialogHeader>
        <DialogTitle>
          {mode === 'create' ? 'Novo campo personalizado' : `Editar: ${field?.label}`}
        </DialogTitle>
      </DialogHeader>

      <DialogContent>
        {/* Passos do assistente — 1 linha rolável no mobile (nunca quebra) */}
        <div className="mb-6 flex items-center gap-1 overflow-x-auto whitespace-nowrap border-b border-line dark:border-line-dark [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {([1, 2, 3, 4] as const).map((s) => {
            const labels = ['1. Básico', '2. Regras', '3. Opções', '4. Aparência'];
            if (s === 3 && !needsOpcoes) return null;
            return (
              <button
                key={s}
                type="button"
                onClick={() => setStep(s as Step)}
                className={`shrink-0 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  step === s
                    ? 'border-red text-ink-1 dark:text-ink-dark-1'
                    : 'border-transparent text-ink-3 dark:text-ink-dark-3 hover:text-ink-1 dark:hover:text-ink-dark-1'
                }`}
              >
                {labels[s - 1]}
              </button>
            );
          })}
        </div>

        {errorMsg && <Alert variant="danger" className="mb-4">{errorMsg}</Alert>}

        {step === 1 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="min-w-0">
              <Label htmlFor="campo-entidade">Cadastro *</Label>
              <Select
                id="campo-entidade"
                value={entidade}
                onChange={(e) => setEntidade(e.target.value)}
                disabled={mode === 'edit'}
              >
                {ENTIDADES_SUPORTADAS.map((ent) => (
                  <option key={ent.value} value={ent.value}>
                    {ent.label}
                  </option>
                ))}
              </Select>
              {/* Onde o campo vai aparecer pra quem preenche — a resposta que
                  faltava. Cadastro sem tela avisa em vez de fingir. */}
              {entidadeMeta?.onde ? (
                <p
                  className="mt-1 text-xs text-ink-3 dark:text-ink-dark-3"
                  data-testid="campo-entidade-onde"
                >
                  Aparece em: {entidadeMeta.onde}
                  {entidadeMeta.nota ? ` ${entidadeMeta.nota}` : ''}
                </p>
              ) : (
                <Alert
                  variant="warning"
                  className="mt-2"
                  data-testid="campo-entidade-sem-tela"
                >
                  {AVISO_ENTIDADE_SEM_TELA}
                  {entidadeMeta?.nota ? ` ${entidadeMeta.nota}` : ''}
                </Alert>
              )}
            </div>
            <div className="min-w-0">
              <Label htmlFor="campo-scope">Onde vale *</Label>
              <Select
                id="campo-scope"
                value={scopeTipo}
                onChange={(e) => setScopeTipo(e.target.value as ScopeTipo)}
              >
                <option value="global">Em todo o sistema</option>
                <option value="workflow">Só num fluxo de trabalho</option>
                <option value="modal">Só numa janela</option>
                <option value="pagina">Só numa tela</option>
              </Select>
            </div>
            {scopeTipo !== 'global' && (
              <div className="min-w-0">
                <Label htmlFor="campo-scope-id">Em qual? *</Label>
                <Input
                  id="campo-scope-id"
                  value={scopeId}
                  onChange={(e) => setScopeId(e.target.value)}
                  placeholder={
                    scopeTipo === 'workflow'
                      ? 'Identificador do fluxo de trabalho'
                      : scopeTipo === 'modal'
                        ? 'Identificador da janela'
                        : 'Identificador da tela'
                  }
                />
              </div>
            )}
            <div className="min-w-0 md:col-span-2">
              <Label htmlFor="campo-label">Nome do campo *</Label>
              <Input
                id="campo-label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Ex.: Origem do lead"
              />
            </div>
            <div className="min-w-0 md:col-span-2">
              <Label htmlFor="campo-key">Identificador *</Label>
              <Input
                id="campo-key"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="origem_lead"
                disabled={mode === 'edit'}
                className="font-mono"
              />
              <p className="text-xs text-ink-3 dark:text-ink-dark-3 mt-1">
                Nome interno do campo, só letras minúsculas, números e _. Não pode ser alterado
                depois.
              </p>
            </div>
            <div className="min-w-0 md:col-span-2">
              <Label htmlFor="campo-descricao">Texto de ajuda</Label>
              <textarea
                id="campo-descricao"
                value={descricao}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDescricao(e.target.value)}
                placeholder="Texto de ajuda mostrado abaixo do campo"
                rows={2}
                className="w-full rounded-lg border border-line dark:border-line-dark bg-paper-surface dark:bg-paper-dark-surface px-3 py-2 text-sm text-ink-1 dark:text-ink-dark-1 focus:outline-none focus:border-red"
              />
            </div>
            <div className="min-w-0 md:col-span-2">
              <Label htmlFor="campo-tipo">Tipo de resposta *</Label>

              {tipoEmPreparacao && (
                <p
                  className="mb-3 flex flex-wrap items-center gap-2 text-xs text-ink-2 dark:text-ink-dark-2"
                  data-testid="campo-tipo-em-preparacao"
                >
                  <Badge variant="warning" size="sm">
                    Em preparação
                  </Badge>
                  Este campo foi criado num tipo que ainda não dá pra preencher —
                  falta o envio de arquivos. Ele continua salvo do jeito que está;
                  escolha outro tipo quando quiser que as pessoas consigam
                  responder.
                </p>
              )}

              {GRUPOS_TIPOS.map((grp) => {
                const tiposDoGrupo = tiposOferecidos.filter((t) => t.grupo === grp.value);
                if (tiposDoGrupo.length === 0) return null;
                return (
                  <div key={grp.value} className="mb-3">
                    <p className="text-xs text-ink-3 dark:text-ink-dark-3 mb-1.5">
                      {grp.label}
                    </p>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
                      {tiposDoGrupo.map((t) => {
                        const emPreparacao = TIPOS_EM_PREPARACAO.includes(t.value);
                        const Icone = t.icone;
                        return (
                          <button
                            key={t.value}
                            type="button"
                            data-testid={`campo-tipo-${t.value}`}
                            disabled={emPreparacao}
                            onClick={() => setTipo(t.value)}
                            className={`min-w-0 text-left px-3 py-2 rounded-lg border transition-colors ${
                              tipo === t.value
                                ? 'border-red bg-red-soft dark:bg-red-soft-dark'
                                : 'border-line dark:border-line-dark hover:border-red/50'
                            } ${emPreparacao ? 'cursor-not-allowed opacity-60' : ''}`}
                          >
                            <span className="flex min-w-0 items-center gap-2">
                              <Icone
                                className="h-4 w-4 shrink-0 text-ink-3 dark:text-ink-dark-3"
                                aria-hidden="true"
                              />
                              <span className="min-w-0 break-words text-sm font-medium text-ink-1 dark:text-ink-dark-1">
                                {t.label}
                              </span>
                            </span>
                            <p className="text-xs text-ink-3 dark:text-ink-dark-3 mt-0.5 break-words">
                              {t.hint}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* PF9 — exposição no formulário público de captação (só lead) */}
            {entidade === 'lead' && (
              <div className="md:col-span-2 rounded-lg border border-line dark:border-line-dark p-4 space-y-3">
                <div className="flex items-center gap-1">
                  <h4 className="text-sm font-semibold text-ink-1 dark:text-ink-dark-1">
                    Formulário de captação
                  </h4>
                  <HelpTip texto="Ligue para o campo aparecer no formulário público de captação (o link /f/... que você divulga). Quem responde é o visitante, sem login. No público funcionam só os tipos Texto curto, Número, Seleção única e Checkbox." />
                </div>
                {/*
                  K-02 — a frase que faltava. O interruptor abaixo é a única
                  coisa no sistema que transforma um campo INTERNO num campo que
                  gente de fora lê e escreve. O rótulo antigo ("Exibir no
                  formulário de captação") descrevia o efeito na tela e calava
                  sobre quem preenche — e ninguém pergunta "isso vaza?" antes de
                  ligar uma chave que parece de exibição.
                */}
                <p className="text-xs text-ink-2 dark:text-ink-dark-2">
                  Campo de lead é interno por padrão: só quem tem acesso ao
                  sistema vê e preenche. Ligar o interruptor abaixo põe o campo
                  numa página aberta na internet.
                </p>
                <label className="flex items-center justify-between gap-3 cursor-pointer">
                  <span className="text-sm text-ink-2 dark:text-ink-dark-2">
                    Exibir no formulário de captação (quem preenche é o visitante)
                  </span>
                  <Switch
                    checked={mostrarNoFormPublico}
                    onChange={(e) => setMostrarNoFormPublico(e.target.checked)}
                    aria-label="Exibir no formulário de captação"
                    data-testid="campo-mostrar-form-publico"
                  />
                </label>
                {mostrarNoFormPublico && (
                  <>
                    {/*
                      O aviso aparece SÓ com o interruptor ligado: avisar antes
                      seria ruído em todo campo de lead, e é justamente depois
                      de ligar que a decisão precisa de uma segunda olhada.
                    */}
                    <Alert variant="warning" data-testid="campo-publico-aviso">
                      O rótulo e o texto de ajuda deste campo ficam visíveis pra
                      qualquer pessoa que abrir o link, e a resposta vem de fora
                      — sem conferência de quem é. Não use pra anotação interna,
                      valor negociado, custo, senha ou qualquer coisa que a
                      equipe não diria ao cliente.
                    </Alert>
                    <label className="flex items-center justify-between gap-3 cursor-pointer">
                      <span className="text-sm text-ink-2 dark:text-ink-dark-2">
                        Obrigatório no formulário
                      </span>
                      <Switch
                        checked={obrigatorioNoFormPublico}
                        onChange={(e) => setObrigatorioNoFormPublico(e.target.checked)}
                        aria-label="Obrigatório no formulário de captação"
                        data-testid="campo-obrigatorio-form-publico"
                      />
                    </label>
                    {!isTipoPublico(tipo) && (
                      <Alert variant="warning">
                        O tipo selecionado não é suportado no formulário público. Use{' '}
                        <strong>Texto curto</strong>, <strong>Número</strong>,{' '}
                        <strong>Seleção única</strong> ou <strong>Checkbox</strong> para que o campo
                        apareça na captação.
                      </Alert>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="min-w-0 md:col-span-2">
              <p className="text-sm text-ink-2 dark:text-ink-dark-2 mb-2">
                Tudo aqui é opcional. Os tipos e-mail, CPF, CNPJ e endereço de site já conferem o
                formato sozinhos.
              </p>
            </div>
            <div className="min-w-0">
              <Label htmlFor="val-min">Valor mínimo (ou mínimo de caracteres)</Label>
              <Input
                id="val-min"
                type="number"
                value={validacaoMin}
                onChange={(e) => setValidacaoMin(e.target.value)}
              />
            </div>
            <div className="min-w-0">
              <Label htmlFor="val-max">Valor máximo (ou máximo de caracteres)</Label>
              <Input
                id="val-max"
                type="number"
                value={validacaoMax}
                onChange={(e) => setValidacaoMax(e.target.value)}
              />
            </div>
            <div className="min-w-0 md:col-span-2">
              <Label htmlFor="val-regex">Padrão exigido no texto (avançado)</Label>
              <Input
                id="val-regex"
                value={validacaoRegex}
                onChange={(e) => setValidacaoRegex(e.target.value)}
                placeholder="^[A-Z]{3}-\d{4}$"
                hint="Expressão regular. Deixe em branco se não souber o que é."
                className="font-mono"
              />
            </div>
            <div className="min-w-0 md:col-span-2">
              <Label htmlFor="val-id">Conferência extra (opcional)</Label>
              <Select
                id="val-id"
                value={validatorId}
                onChange={(e) => setValidatorId(e.target.value)}
              >
                <option value="">Nenhuma</option>
                {validators.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.nome} — {v.descricao ?? v.tipo}
                  </option>
                ))}
              </Select>
              <p className="text-xs text-ink-3 dark:text-ink-dark-3 mt-1">
                O sistema já vem com conferências prontas de CPF, CNPJ, CEP, e-mail, telefone e
                endereço de site.
              </p>
            </div>
          </div>
        )}

        {step === 3 && needsOpcoes && (
          <OpcoesEditor opcoes={opcoes} setOpcoes={setOpcoes} />
        )}

        {step === 4 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="min-w-0">
              <Label htmlFor="ui-placeholder">Texto de exemplo dentro do campo</Label>
              <Input
                id="ui-placeholder"
                value={placeholder}
                onChange={(e) => setPlaceholder(e.target.value)}
              />
            </div>
            <div className="min-w-0">
              <Label htmlFor="ui-grupo">Grupo</Label>
              <Input
                id="ui-grupo"
                value={grupo}
                onChange={(e) => setGrupo(e.target.value)}
                placeholder="Ex.: Financeiro, Identificação"
              />
            </div>
            <div className="min-w-0">
              <Label htmlFor="ui-prefix">Antes do valor</Label>
              <Input
                id="ui-prefix"
                value={prefix}
                onChange={(e) => setPrefix(e.target.value)}
                placeholder="R$, @"
              />
            </div>
            <div className="min-w-0">
              <Label htmlFor="ui-suffix">Depois do valor</Label>
              <Input
                id="ui-suffix"
                value={suffix}
                onChange={(e) => setSuffix(e.target.value)}
                placeholder="%, kg"
              />
            </div>
            <div className="min-w-0">
              <Label htmlFor="ui-largura">Largura no formulário</Label>
              <Select
                id="ui-largura"
                value={largura}
                onChange={(e) => setLargura(e.target.value as Largura)}
              >
                <option value="full">A linha toda</option>
                <option value="half">Metade da linha</option>
                <option value="third">Um terço da linha</option>
                <option value="quarter">Um quarto da linha</option>
              </Select>
            </div>
            <div className="min-w-0">
              <Label htmlFor="ui-ordem">Posição na lista</Label>
              <Input
                id="ui-ordem"
                type="number"
                value={ordem}
                onChange={(e) => setOrdem(Number(e.target.value))}
                hint="Menor número aparece primeiro."
              />
            </div>
            <div className="min-w-0 md:col-span-2">
              <Label>Quem enxerga o campo (nenhum marcado = todos)</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {PERFIS_DISPONIVEIS.map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => togglePerfil(visiveis, setVisiveis, p.value)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                      visiveis.includes(p.value)
                        ? 'border-red bg-red-soft dark:bg-red-soft-dark text-ink-1 dark:text-ink-dark-1'
                        : 'border-line dark:border-line-dark text-ink-2 dark:text-ink-dark-2'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="min-w-0 md:col-span-2">
              <Label>Quem pode preencher (nenhum marcado = quem vê pode preencher)</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {PERFIS_DISPONIVEIS.map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => togglePerfil(editaveis, setEditaveis, p.value)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                      editaveis.includes(p.value)
                        ? 'border-red bg-red-soft dark:bg-red-soft-dark text-ink-1 dark:text-ink-dark-1'
                        : 'border-line dark:border-line-dark text-ink-2 dark:text-ink-dark-2'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </DialogContent>

      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          Cancelar
        </Button>
        {step > 1 && (
          <Button
            variant="ghost"
            onClick={() => setStep((s) => (s - 1) as Step)}
          >
            Voltar
          </Button>
        )}
        {step < 4 ? (
          <Button
            onClick={() => {
              // Se está no step 2 e não precisa opções, pula pra 4
              if (step === 2 && !needsOpcoes) {
                setStep(4);
              } else {
                setStep((s) => (s + 1) as Step);
              }
            }}
          >
            Próximo
          </Button>
        ) : (
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Salvando…' : mode === 'create' ? 'Criar campo' : 'Salvar'}
          </Button>
        )}
      </DialogFooter>
    </Dialog>
  );
}

function OpcoesEditor({
  opcoes,
  setOpcoes,
}: {
  opcoes: OpcaoItem[];
  setOpcoes: (o: OpcaoItem[]) => void;
}) {
  function add() {
    setOpcoes([...opcoes, { value: '', label: '' }]);
  }
  function remove(i: number) {
    setOpcoes(opcoes.filter((_, idx) => idx !== i));
  }
  function update(i: number, patch: Partial<OpcaoItem>) {
    setOpcoes(opcoes.map((op, idx) => (idx === i ? { ...op, ...patch } : op)));
  }
  return (
    <div>
      <p className="text-sm text-ink-2 dark:text-ink-dark-2 mb-3">
        Opções da lista. O <strong>valor guardado</strong> é o que fica registrado no sistema; o{' '}
        <strong>texto na tela</strong> é o que a pessoa lê.
      </p>
      <div className="space-y-2">
        {opcoes.map((op, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2">
            <Input
              value={op.value}
              onChange={(e) => update(i, { value: e.target.value })}
              placeholder="valor guardado"
              aria-label={`Valor guardado da opção ${i + 1}`}
              wrapperClassName="min-w-0 flex-1 basis-[140px]"
              className="font-mono text-sm"
            />
            <Input
              value={op.label}
              onChange={(e) => update(i, { label: e.target.value })}
              placeholder="texto na tela"
              aria-label={`Texto na tela da opção ${i + 1}`}
              wrapperClassName="min-w-0 flex-1 basis-[140px]"
            />
            <Button variant="ghost" size="sm" onClick={() => remove(i)}>
              Remover
            </Button>
          </div>
        ))}
      </div>
      <Button variant="ghost" onClick={add} className="mt-3">
        Adicionar opção
      </Button>
    </div>
  );
}
